﻿import React, { useState, useEffect } from 'react';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { SampleType } from '../../models/SamplePreparations/sample-type';
import { ToastProps } from '../../models/toast-props';
import { CrudFunctions } from '../../utilities/crud-functions';
import DataTemplates from '../../templates/data-templates';
import { Technology } from '../../models/technology';

import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import Button from 'devextreme-react/button';
import List from 'devextreme-react/list';
import Toast from 'devextreme-react/toast';

let key = 0;

export interface Props {
    sampleTypeId?: number;
    technologies: Technology[];
    isNew: boolean;
    onClose?: () => void;
}

export default function SampleTypesEditor(props: Props) {
    const [sampleType, setSampleType] =
        useState<SampleType>();

    const [selectedTechnologies, setSelectedTechnologies] =
        useState<number[]>([]);

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        getAllData();
    }, [props.sampleTypeId]);

    key++;

    return (
        <div>
            <div className="">
                <Button id="saveButton" text="Save" icon="save" type="success"
                    className="pw-button"
                    hint="save changes"
                    height='1.8rem'
                    onClick={onSaveClick} />

                <Button id="closeButton" text="Close" icon="close" type="normal"
                    className="pw-button"
                    hint="close dialog"
                    height='1.8rem'
                    onClick={onCloseClick} />
            </div>

            <div>
                <div className="pw-form-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Name</div>
                        <TextBox id="name" className="textbox"
                            inputAttr={{ id: "txtName" }}
                            onValueChanged={onValueChanged}
                            placeholder='NA'
                            value={sampleType && sampleType.name ? sampleType.name : undefined} />
                    </div>

                    <div className="pw-form-unit pw-comp-10">
                        <div className="pw-form-unit-label">Sequence</div>
                        <NumberBox id="sequence" className="textbox"
                            inputAttr={{ id: "txtSequence" }}
                            onValueChanged={onValueChanged}
                            placeholder='NA'
                            value={sampleType && sampleType.sequence ? sampleType.sequence : undefined} />
                    </div>
                </div>

                <div className="pw-form-unit">
                    <div className="pw-form-unit-label">Description</div>
                    <TextBox id="description" className="textbox"
                        inputAttr={{ id: "txtDescription" }}
                        onValueChanged={onValueChanged}
                        placeholder='NA'
                        value={sampleType && sampleType.description ? sampleType.description : undefined} />
                </div>
            </div>

            <div className="pw-form-row">
                <div className="pw-comp-20 pw-margin-left-1">
                    <div className="pw-form-unit-label">Technologies</div>

                    <List id="assetTechnologiesList"
                        height="16rem"
                        className="pw-bg-white"
                        items={props.technologies ? props.technologies : []}
                        keyExpr="technologyId"
                        showScrollbar="always"
                        itemRender={DataTemplates.EhsTemplate}
                        showSelectionControls={true}
                        selectionMode="multiple"
                        onSelectionChanged={(e) => {
                            setSelectedTechnologies(e.component.option('selectedItemKeys'));
                        }}
                        selectedItemKeys={selectedTechnologies} />
                </div>
            </div>

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onClose={onGlobalDialogClose} />
            }

        </div>
    );

    async function onSaveClick() {
        //console.log("onSaveClick()");
        //console.log(sampleType);
        //console.log(selectedTechnologies);

        let editedSampleType: SampleType = { ...sampleType };

        editedSampleType.technologies = selectedTechnologies;

        if (props.isNew) {
            await insertSampleType(editedSampleType)
                .then(data => {
                    showToast("Sample Type added successfully", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting New Sample Type");
                });
        }
        else {
            await updateSampleType(editedSampleType)
                .then(data => {
                    showToast("Sample Type updated successfully", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Sample Type");
                });
        }
    }

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    function onValueChanged(e: any) {
        const caller = e.element.id as string;

        setSampleType({ ...sampleType, [caller]: e.value });
    }

    async function getSampleType() {
        if (props.sampleTypeId) {
            await CrudFunctions.getData(`common/sampletype/${props.sampleTypeId}`)
                .then(data => {
                    setSampleType(data);

                    let sampleTechnologies = data.technologies;
                    setSelectedTechnologies(sampleTechnologies);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Sample Type");
                });
        }
    }

    async function insertSampleType(editedSampleType: SampleType) {
        return await CrudFunctions.postData("common/sampletype", editedSampleType);
    }

    async function updateSampleType(editedSampleType: SampleType) {
        return await CrudFunctions.putData("common/sampletype", editedSampleType);
    }

    async function getAllData() {
        if (props.isNew) {
            let newSampleType: SampleType = {};

            setSampleType(newSampleType);

            return;
        }

        await Promise.all([
            getSampleType()
        ])
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}